// The Word Explorer: tap any Prakrit word anywhere to see its analysis, formation, table and links.

import type { FC, ReactNode } from 'react'
import type { WordAnalysisInfo, WordInfo } from '@/types/word'

import Link from 'next/link'
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react'
import {
    BookOpen,
    Dumbbell,
    ListPlus,
    Loader2,
    Table2
} from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { useMediaQuery } from '@/hooks/use-media-query'
import { useProgress } from '@/prakrit/lib/client/progress'
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle
} from '@/components/ui/sheet'
import { Bi } from '@/prakrit/components/common/bi'
import { AudioButton } from '@/prakrit/components/common/audio-button'
import { WordPicture } from '@/prakrit/components/common/word-picture'
import { StatusBadge, reviewBadgeKind } from '@/prakrit/components/common/status-badge'
import { FormationSteps } from '@/prakrit/components/grammar/formation-steps'
import { DeclensionGrid, GrammarGrid } from '@/prakrit/components/grammar/grammar-grid'

interface WordPanelContextValue {
    open: (word: string) => void
    close: () => void
}

const WordPanelContext = createContext<WordPanelContextValue | null>(null)

const cache = new Map<string, WordInfo>()

export const fetchWordInfo = async (word: string): Promise<WordInfo> => {
    const key = word.normalize('NFC')
    const hit = cache.get(key)

    if (hit) return hit

    const res = await fetch(`/api/word?form=${encodeURIComponent(key)}`)

    if (!res.ok) throw new Error('lookup failed')

    const info = await res.json() as WordInfo

    cache.set(key, info)

    return info
}

const ActionLink: FC<{ href: string, hi: string, en: string, icon: ReactNode, onNavigate?: () => void }> = ({ href, hi, en, icon, onNavigate }): ReactNode => (
    <Link
        href={href}
        onClick={onNavigate}
        className='inline-flex min-h-tap items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm font-medium hover:border-saffron/60 hover:bg-saffron/5'
    >
        {icon}
        <Bi hi={hi} en={en} layout='inline' enClassName='text-muted-foreground' />
    </Link>
)

export const AnalysisCard: FC<{ analysis: WordAnalysisInfo, form: string, onNavigate?: () => void, showGrid?: boolean }> = ({
    analysis: a,
    form,
    onNavigate,
    showGrid = true
}): ReactNode => {
    const { addToReview } = useProgress()

    const add = () => {
        const added = addToReview({ ref: a.ref, form, note: a.cells.map(c => c.hi).join(', ') })

        if (added) toast.success('दोहराव सूची में जोड़ा गया · Added to your review list')
        else toast('यह पहले से दोहराव सूची में है · Already in your review list')
    }

    return (
        <article className='flex flex-col gap-4 rounded-xl border border-border bg-card p-4 shadow-card'>
            <header className='flex flex-wrap items-start justify-between gap-2'>
                <div className='flex min-w-0 items-start gap-3'>
                    <WordPicture pos={a.pos} word={a.lemma} decorative sizes='64px' className='size-16 shrink-0 rounded-xl' />
                    <div className='flex min-w-0 flex-col gap-1'>
                        <p className='flex flex-wrap items-baseline gap-x-2'>
                            <span lang='pra' className='pra pra-md'>{a.lemmaLabel}</span>
                            <Bi hi={a.meaning.hi} en={a.meaning.en} layout='inline' className='text-sm' />
                        </p>
                        <ul className='flex flex-wrap gap-1.5'>
                            {a.tags.map((t, i) => (
                                <li key={i} className='rounded-md bg-secondary px-2 py-0.5 text-xs'>
                                    <span lang='hi' className='hindi font-medium'>{t.hi}</span>
                                    <span lang='en' data-ex='en' className='text-muted-foreground'> · {t.en}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
                <div className='flex flex-wrap gap-1'>
                    {a.status === 'alt' && <StatusBadge kind='alt' />}
                    {a.review !== 'verified' && a.review !== 'published' && <StatusBadge kind={reviewBadgeKind(a.review)} />}
                    {(a.review === 'verified' || a.review === 'published') && <StatusBadge kind='verified' />}
                </div>
            </header>

            {a.cells.length > 0 && (
                <ul className='flex flex-col gap-0.5 text-sm'>
                    {a.cells.map((c, i) => (
                        <li key={i}><Bi hi={c.hi} en={c.en} layout='inline' /></li>
                    ))}
                </ul>
            )}

            {a.status === 'alt' && a.coreForm && (
                <p className='rounded-lg bg-terracotta/8 px-3 py-2 text-sm'>
                    <Bi
                        hi={<>यह विकल्प रूप है — सही माना जाता है। पहले सिखाया जाने वाला रूप <span lang='pra' className='font-prakrit font-semibold text-prakrit'>{a.coreForm}</span> है।</>}
                        en={<>A variant form, accepted as correct. The form taught first is <span lang='pra' className='font-prakrit font-semibold text-prakrit'>{a.coreForm}</span>.</>}
                    />
                </p>
            )}

            {a.formation && (
                <section aria-label='रूप-निर्माण · How it is built' className='flex flex-col gap-2'>
                    <h3 className='text-sm font-semibold'>
                        <Bi hi='रूप कैसे बना' en='How it is built' layout='inline' />
                    </h3>
                    <FormationSteps formation={a.formation} size='sm' />
                </section>
            )}

            {showGrid && a.verbGrid && (
                <section className='flex flex-col gap-2'>
                    <h3 className='text-sm font-semibold'><Bi hi={a.verbGrid.title.hi} en={a.verbGrid.title.en} layout='inline' /></h3>
                    <GrammarGrid cells={a.verbGrid.cells} highlight={a.verbGrid.highlight} />
                </section>
            )}

            {showGrid && a.caseGrid && (
                <section className='flex flex-col gap-2'>
                    <h3 className='text-sm font-semibold'><Bi hi='विभक्ति-तालिका' en='Case table' layout='inline' /></h3>
                    <DeclensionGrid cells={a.caseGrid.cells} cases={a.caseGrid.cases} highlight={a.caseGrid.highlight} showKarak={false} />
                </section>
            )}

            {a.sanskritCognate && (
                <p className='text-sm text-muted-foreground'>
                    <span lang='hi' className='hindi'>संस्कृत: </span>
                    <span lang='sa' className='font-prakrit'>{a.sanskritCognate}</span>
                </p>
            )}

            <div className='flex flex-wrap gap-2'>
                {a.links.table && <ActionLink href={a.links.table.href} hi='पूरी तालिका' en='Full table' icon={<Table2 className='size-4' />} onNavigate={onNavigate} />}
                {a.links.lesson && <ActionLink href={a.links.lesson.href} hi='यह रूप सीखें' en='Learn this form' icon={<BookOpen className='size-4' />} onNavigate={onNavigate} />}
                {a.links.practice && <ActionLink href={a.links.practice.href} hi='अभ्यास करें' en='Practise' icon={<Dumbbell className='size-4' />} onNavigate={onNavigate} />}
                <button
                    type='button'
                    onClick={add}
                    className='inline-flex min-h-tap items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm font-medium hover:border-saffron/60 hover:bg-saffron/5'
                >
                    <ListPlus className='size-4' />
                    <Bi hi='दोहराव में जोड़ें' en='Add to review' layout='inline' enClassName='text-muted-foreground' />
                </button>
            </div>
        </article>
    )
}

export const WordDetail: FC<{ info: WordInfo, onNavigate?: () => void, showGrid?: boolean }> = ({ info, onNavigate, showGrid = true }): ReactNode => (
    <div className='flex flex-col gap-4'>
        <div className='flex flex-col gap-1'>
            <p lang='pra' className='pra pra-xl'>{info.form}</p>
            <p lang='pra-Latn' className='translit text-sm italic text-muted-foreground'>{info.translit}</p>
            <AudioButton text={info.form} />
        </div>

        {!info.known && (
            <p className='rounded-lg border border-dashed border-border p-4 text-sm'>
                <Bi
                    hi='यह शब्द अभी हमारी शब्द-सूची और रूप-तालिकाओं में नहीं है। हम अनुमान से कोई विश्लेषण नहीं दिखाते।'
                    en='This word is not in our word list or tables yet. We never show a guessed analysis.'
                />
            </p>
        )}

        {info.analyses.length > 1 && (
            <p className='text-sm text-muted-foreground'>
                <Bi hi={`इस रूप के ${info.analyses.length} विश्लेषण हैं — वाक्य का अर्थ बताता है कि कौन-सा सही है।`} en={`This form has ${info.analyses.length} readings — the sentence decides which one is meant.`} />
            </p>
        )}

        {info.analyses.map(a => <AnalysisCard key={a.id} analysis={a} form={info.form} onNavigate={onNavigate} showGrid={showGrid} />)}
    </div>
)

export const WordPanelProvider = ({ children }: { children: ReactNode }): ReactNode => {
    const [word, setWord] = useState<string | null>(null)
    const [open, setOpen] = useState(false)
    const [info, setInfo] = useState<WordInfo | null>(null)
    const [error, setError] = useState(false)
    const wide = useMediaQuery('(min-width: 768px)')
    const { addRecent } = useProgress()

    useEffect(() => {
        if (!word) return

        let cancelled = false

        setInfo(null)
        setError(false)

        fetchWordInfo(word)
            .then(result => {
                if (cancelled) return

                setInfo(result)

                if (result.known) addRecent(result.form)
            })
            .catch(() => {
                if (!cancelled) setError(true)
            })

        return () => {
            cancelled = true
        }
    }, [word, addRecent])

    const openWord = useCallback((w: string) => {
        setWord(w.normalize('NFC').replace(/[।॥?!,;:“”"‘’()—–]/g, '').trim())
        setOpen(true)
    }, [])

    const close = useCallback(() => setOpen(false), [])

    const value = useMemo(() => ({ open: openWord, close }), [openWord, close])

    return (
        <WordPanelContext.Provider value={value}>
            {children}
            <Sheet open={open} onOpenChange={setOpen}>
                <SheetContent
                    side={wide ? 'right' : 'bottom'}
                    className={cn('gap-0 overflow-y-auto', wide ? 'w-full sm:max-w-md' : 'max-h-[88vh] rounded-t-2xl')}
                >
                    <SheetHeader className='border-b border-border'>
                        <SheetTitle>
                            <Bi hi='शब्द-विवरण' en='Word Explorer' layout='inline' always />
                        </SheetTitle>
                        <SheetDescription className='sr-only'>Analysis of the Prakrit word {word}</SheetDescription>
                    </SheetHeader>
                    <div className='p-4' aria-live='polite'>
                        {!info && !error && (
                            <p className='flex items-center gap-2 text-sm text-muted-foreground'>
                                <Loader2 className='size-4 animate-spin' />
                                <span lang='hi' className='hindi'>खोज रहे हैं…</span>
                            </p>
                        )}
                        {error && (
                            <p className='text-sm'>
                                <Bi hi='अभी विवरण लोड नहीं हो सका। कृपया फिर से टैप करें।' en='The details could not load right now. Please tap the word again.' />
                            </p>
                        )}
                        {info && <WordDetail info={info} onNavigate={close} />}
                    </div>
                </SheetContent>
            </Sheet>
        </WordPanelContext.Provider>
    )
}

export const useWordPanel = (): WordPanelContextValue => {
    const ctx = useContext(WordPanelContext)

    if (!ctx) throw new Error('useWordPanel must be used inside WordPanelProvider')

    return ctx
}
